/**
 * useStudentAvatarAI — « Générer un avatar IA » de « Mon compte » : la feuille, ses deux façons
 * d'avoir une photo, l'envoi, puis la relève du statut jusqu'à l'avatar prêt.
 *
 * Les gestes et les appels de `GenerateAIAvatarModal.js` (que `CustomizableAvatar.js` montait sur
 * l'écran du compte de l'élève) :
 *  - « Prendre une photo » : l'appareil photo de l'app (`camera`, `formId` `avatar_generation`, sans
 *    GeniusScan), la photo revenant par `camera.lastPhoto` ; la feuille se cache le temps du passage ;
 *  - « Choisir une photo » : la galerie, recadrée au carré ;
 *  - « Générer avec ma photo » : `startAvatarGeneration` (l'avatar travaille aussitôt), la photo en
 *    data URI, `POST students/{id}/generate-profile-image` (`source_image`) ; au succès la feuille
 *    se referme, l'avis dit « Génération lancée » et la relève commence ; un refus se lit dans la
 *    feuille (la limite du mois, l'accès refusé, la phrase du serveur) ;
 *  - la relève : `GET students/{id}/profile-image-status` toutes les 3 s, 90 s au plus ; prêt → le
 *    compte relu (`getUser`), puis l'avatar ne travaille plus ; refusé ou en échec → l'avis ;
 *  - à l'ouverture de l'écran, le statut lu une fois : une génération lancée avant (l'écran quitté,
 *    l'app rechargée) reprend sa relève ;
 *  - `openAIAvatarModal` (la relance « Personnalise ton profil » pousse `account` avec) ouvre la
 *    feuille d'office, une fois, puis le paramètre retombe.
 * L'écran quitté, la relève s'arrête et l'avatar ne travaille plus, comme avant.
 */
import { useNavigation } from "@react-navigation/native"
import * as ImagePicker from "expo-image-picker"
import { useCallback, useEffect, useMemo, useReducer, useRef } from "react"
import { useDispatch, useSelector } from "react-redux"

import { apiRequest } from "../../../../../../data/redux/actions/api"
import { clearLastPhoto } from "../../../../../../data/redux/actions/camera"
import { startAvatarGeneration, stopAvatarGeneration } from "../../../../../../data/redux/actions/data"
import { authService } from "../../../../../../data/services/authService"
import { studentsService } from "../../../../../../data/services/studentsService"
import { type AccountAvatarAIModalProps } from "../../../../../../design-system/templates/AccountAvatarAIModal/AccountAvatarAIModal"
import { apiCall, apiFailureOf, requestOptionsFor } from "../../../../../../engine/shared/apiCall"
import { studentRefusalText } from "../../../hooks/studentRefusal"
import { useGesture } from "../../../hooks/gestureTracking"
import { useMediaPickerAdapter } from "../../../hooks/useMediaPickerAdapter"
import {
    AVATAR_CAMERA_FORM_ID,
    GENERATION_STARTED,
    STATUS_POLL_EVERY_MS,
    STATUS_POLL_TIMEOUT_MS,
    STATUS_READ_FAILED,
    STATUS_TIMED_OUT,
    avatarAIReducer,
    avatarGenerationInfo,
    generationRefusal,
    initialAvatarAIState,
    statusOutcome,
    type ApiProfileImageStatus,
} from "./avatarAIGeneration"
import { selectAvatarGeneration, selectDisplayedStudent } from "../../../../view-models/shared/displayedStudent"

const PICK_FAILED = "Impossible de sélectionner l'image. Réessaye."
const CONVERSION_FAILED = "Impossible de générer l'avatar. Réessaye."

/** La photo en data URI, comme l'attend le serveur : l'appareil photo du web en donne déjà une. */
async function toDataUri(uri: string): Promise<string> {
    if (uri.startsWith("data:")) return uri
    const blob = await (await fetch(uri)).blob()
    return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onloadend = () => resolve(String(reader.result))
        reader.onerror = () => reject(new Error("Erreur lors de la conversion de l'image"))
        reader.readAsDataURL(blob)
    })
}

/* eslint-disable @typescript-eslint/no-explicit-any */
const selectStudentId = (state: any): number | null => selectDisplayedStudent(state)?.id ?? null
const selectRemaining = (state: any): number | null => {
    const value = selectDisplayedStudent(state)?.avatar_generations_remaining
    return typeof value === "number" ? value : null
}
const selectGenerating = (state: any): boolean => !!selectAvatarGeneration(state)?.isGenerating
const selectCameraOn = (state: any): boolean => !!state?.activity?.cameraOn
/* eslint-enable @typescript-eslint/no-explicit-any */

type Navigation = { setParams: (params: object) => void }

export function useStudentAvatarAI(options: { openOnArrival?: boolean } = {}) {
    const { openOnArrival = false } = options
    const dispatch = useDispatch()
    const navigation = useNavigation() as unknown as Navigation
    const studentId = useSelector(selectStudentId)
    const serverRemaining = useSelector(selectRemaining)
    const generating = useSelector(selectGenerating)
    const cameraOn = useSelector(selectCameraOn)
    const [state, dispatchUi] = useReducer(avatarAIReducer, serverRemaining, initialAvatarAIState)

    const { launchCamera, incomingCameraImages, onCameraImagesConsumed } = useMediaPickerAdapter({
        formId: AVATAR_CAMERA_FORM_ID,
        geniusScan: false,
    })

    // Les rappels de la relève lisent la valeur du moment, pas celle de leur création.
    const generatingRef = useRef(generating)
    generatingRef.current = generating
    const studentIdRef = useRef(studentId)
    studentIdRef.current = studentId

    useEffect(() => dispatchUi({ type: "REMAINING", remaining: serverRemaining }), [serverRemaining])

    // La photo de l'appareil photo, revenue par le store pour cette feuille.
    useEffect(() => {
        const photo = incomingCameraImages[0]
        if (photo?.uri == null) return
        if (state.open) dispatchUi({ type: "PHOTO", uri: photo.uri })
        onCameraImagesConsumed()
        // eslint-disable-next-line react-hooks/exhaustive-deps -- seulement quand une photo arrive
    }, [incomingCameraImages])

    // ── La relève du statut ─────────────────────────────────────────────────────────────────
    type Poll = { id: number; every: ReturnType<typeof setInterval>; timeout: ReturnType<typeof setTimeout> }
    const pollRef = useRef<Poll | null>(null)
    const pollCountRef = useRef(0)
    /**
     * Ce qui rend périmée une lecture hors relève (celle de l'ouverture de l'écran) : une génération
     * lancée ou une relève démarrée depuis sa demande. Sa réponse « rien en cours », demandée avant,
     * arrêtait la relève de la génération qui venait de partir.
     */
    const epochRef = useRef(0)
    const unmountedRef = useRef(false)
    /** Un envoi en vol : un second toucher sur « Générer » avant le rendu suivant ne repart pas. */
    const sendingRef = useRef(false)
    const gesture = useGesture("avatar.generate")

    const clearPolling = useCallback(() => {
        if (pollRef.current == null) return
        clearInterval(pollRef.current.every)
        clearTimeout(pollRef.current.timeout)
        pollRef.current = null
    }, [])

    const stopPolling = useCallback(() => {
        clearPolling()
        dispatch(stopAvatarGeneration() as never)
    }, [clearPolling, dispatch])

    const readStatusRef = useRef<(fromPoll?: number) => void>(() => {})

    const startPolling = useCallback(() => {
        clearPolling()
        pollCountRef.current += 1
        epochRef.current += 1
        const id = pollCountRef.current
        pollRef.current = {
            id,
            every: setInterval(() => readStatusRef.current(id), STATUS_POLL_EVERY_MS),
            timeout: setTimeout(() => {
                if (pollRef.current?.id !== id) return
                stopPolling()
                dispatchUi({ type: "NOTICE", notice: STATUS_TIMED_OUT })
            }, STATUS_POLL_TIMEOUT_MS),
        }
    }, [clearPolling, stopPolling])

    /**
     * Une lecture du statut. Celle d'une relève (`fromPoll`) ne vaut que tant que SA relève court : une
     * réponse arrivée après l'arrêt (la limite des 90 s, un autre statut lu entre-temps) relançait la
     * relève sur un « en cours » périmé, sans fin.
     */
    const readStatus = useCallback(
        (fromPoll?: number) => {
            const id = studentIdRef.current
            if (id == null) return
            const epoch = epochRef.current
            const stale = () => (fromPoll != null ? pollRef.current?.id !== fromPoll : epochRef.current !== epoch)
            apiCall<ApiProfileImageStatus>(dispatch as never, studentsService.getProfileImageStatus, {
                routeParams: { id },
                whole: true,
            })
                .then((data) => {
                    if (unmountedRef.current || stale()) return
                    dispatchUi({ type: "REMAINING", remaining: data?.generations_remaining })
                    const outcome = statusOutcome(data, generatingRef.current)
                    if (outcome.kind === "follow") {
                        if (pollRef.current == null) {
                            dispatch(startAvatarGeneration() as never)
                            startPolling()
                        }
                        return
                    }
                    if (outcome.kind === "done") {
                        // L'avatar travaille jusqu'à ce que le compte relu le montre. La relecture passe
                        // par `apiRequest` (et non `apiCall`, qui remet la tranche au repos) : « Mon
                        // compte » lit la tranche `getUser`.
                        clearPolling()
                        dispatch(
                            apiRequest(authService.getUser, {}, {}, {}, null, null, {
                                ...requestOptionsFor(false),
                                onResult: () => dispatch(stopAvatarGeneration() as never),
                            }) as never
                        )
                    } else {
                        stopPolling()
                    }
                    if (outcome.notice != null) dispatchUi({ type: "NOTICE", notice: outcome.notice })
                })
                .catch(() => {
                    if (unmountedRef.current || stale()) return
                    const wasGenerating = generatingRef.current
                    stopPolling()
                    if (wasGenerating) dispatchUi({ type: "NOTICE", notice: STATUS_READ_FAILED })
                })
        },
        [dispatch, startPolling, clearPolling, stopPolling]
    )
    readStatusRef.current = readStatus

    // Le statut lu une fois à l'ouverture de l'écran ; l'écran quitté, la relève s'arrête.
    useEffect(() => {
        unmountedRef.current = false
        readStatusRef.current()
        return () => {
            unmountedRef.current = true
            stopPolling()
        }
    }, [stopPolling])

    // La relance « Personnalise ton profil » ouvre la feuille d'office, une fois : le paramètre ne
    // retombe qu'au rendu suivant.
    const openedOnArrivalRef = useRef(false)
    useEffect(() => {
        if (!openOnArrival || openedOnArrivalRef.current) return
        openedOnArrivalRef.current = true
        dispatchUi({ type: "OPEN" })
        dispatch(clearLastPhoto() as never)
        navigation.setParams({ openAIAvatarModal: false })
    }, [openOnArrival, dispatch, navigation])

    // ── Les gestes ──────────────────────────────────────────────────────────────────────────
    const open = useCallback(() => {
        gesture.opened()
        dispatchUi({ type: "OPEN" })
        dispatch(clearLastPhoto() as never)
    }, [dispatch, gesture])

    const pickPhoto = useCallback(async () => {
        try {
            const result = await ImagePicker.launchImageLibraryAsync({
                mediaTypes: ["images"],
                allowsEditing: true,
                aspect: [1, 1],
                quality: 1,
            })
            const uri = result.canceled ? null : result.assets?.[0]?.uri
            if (uri != null) dispatchUi({ type: "PHOTO", uri })
        } catch {
            dispatchUi({ type: "REFUSED", message: PICK_FAILED })
        }
    }, [])

    const generate = useCallback(async () => {
        const id = studentIdRef.current
        if (state.photoUri == null || id == null || state.sending || sendingRef.current) return
        sendingRef.current = true
        epochRef.current += 1
        dispatchUi({ type: "SEND" })
        dispatch(startAvatarGeneration() as never)
        // La photo lue avant l'envoi peut échouer : seul un envoi parti est un geste soumis.
        let submitted = false
        try {
            const sourceImage = await toDataUri(state.photoUri)
            gesture.submitted()
            submitted = true
            const data = await apiCall<{ generations_remaining?: number | null }>(
                dispatch as never,
                studentsService.generateProfileImage,
                { routeParams: { id }, data: { source_image: sourceImage }, whole: true }
            )
            gesture.succeeded()
            if (unmountedRef.current) return
            dispatchUi({ type: "SENT", remaining: data?.generations_remaining })
            dispatchUi({ type: "NOTICE", notice: GENERATION_STARTED })
            startPolling()
        } catch (cause) {
            if (submitted) gesture.failed(cause)
            dispatch(stopAvatarGeneration() as never)
            if (unmountedRef.current) return
            // Le serveur a refusé, ou la photo n'a pas pu être lue avant l'envoi (`toDataUri`).
            const failure = apiFailureOf(cause)
            dispatchUi({
                type: "REFUSED",
                message:
                    failure != null
                        ? generationRefusal(failure)
                        : studentRefusalText(cause instanceof Error ? cause.message : null, CONVERSION_FAILED),
            })
        } finally {
            sendingRef.current = false
        }
    }, [dispatch, state.photoUri, state.sending, startPolling, gesture])

    const info = useMemo(() => avatarGenerationInfo(generating, state.remaining), [generating, state.remaining])

    const sheet = useMemo<AccountAvatarAIModalProps>(
        () => ({
            visible: state.open,
            onClose: () => dispatchUi({ type: "CLOSE" }),
            photoUri: state.photoUri,
            onTakePhoto: () => launchCamera(),
            onPickPhoto: () => void pickPhoto(),
            onGenerate: () => void generate(),
            sending: state.sending,
            generateDisabled: info.blocking,
            info,
            error: state.error,
            hidden: cameraOn,
        }),
        [state.open, state.photoUri, state.sending, state.error, info, cameraOn, launchCamera, pickPhoto, generate]
    )

    return { open, sheet, notice: state.notice, dismissNotice: () => dispatchUi({ type: "DISMISS_NOTICE" }) }
}
